import { useState } from 'react';

import {
  KnowledgeSources,
  SettingsDrillRow,
  SettingsGroup,
  SettingsPageFrame,
} from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Icon,
  LoadingIndicator,
  Separator,
  useToast,
} from '@eluve/components';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import { useChatSettings } from './useChatSettings';

export const ChatSettingsPage = () => {
  const {
    tenantId,
    contextSources,
    files,
    preferences,
    resetSettings,
    saveContextSources,
    saveFiles,
  } = useChatSettings();
  const toast = useToast();
  const knowledgeBasesEnabled = useIsPostHogFeatureFlagEnabled(
    'CHAT_KNOWLEDGE_BASES',
  );

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleResetSettings = async () => {
    try {
      setIsResetting(true);
      await resetSettings();
      setShowResetDialog(false);
      toast.success('Settings reset to defaults');
    } catch {
      toast.error('Failed to reset settings');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <SettingsPageFrame title="Chat Preferences">
      {isResetting ? (
        <LoadingIndicator size="xl" />
      ) : (
        <>
          <SettingsGroup title="Preferences">
            <SettingsDrillRow
              title="Output Preferences"
              description={
                preferences.length > 0
                  ? `${preferences.length} saved ${preferences.length === 1 ? 'preference' : 'preferences'} for response format and detail level.`
                  : 'Configure response format, detail level, and custom instructions.'
              }
              to="output-preferences"
            />
            <Separator className="my-0" />
            <SettingsDrillRow
              title="Custom Instructions"
              description="Extra guidance every chat response should follow."
              to="custom-instructions"
            />
          </SettingsGroup>

          <SettingsGroup title="Conversation Starters">
            <SettingsDrillRow
              title="Conversation Starters"
              description="Prompts suggested when starting a new chat."
              to="conversation-starters"
            />
          </SettingsGroup>

          <SettingsGroup
            title="Knowledge Base"
            description="Select which knowledge sources to include in chat context."
          >
            <KnowledgeSources
              tenantId={tenantId}
              initialSources={contextSources}
              initialFiles={files}
              onSave={saveContextSources}
              onSaveFiles={saveFiles}
              showFileUploads={knowledgeBasesEnabled}
            />
          </SettingsGroup>

          <div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowResetDialog(true)}
            >
              <Icon name="RotateCcw" size="auto" />
              Reset to Default
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all your custom chat settings including custom
              instructions, conversation starters, and uploaded files. The chat
              will revert to default settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetSettings}>
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageFrame>
  );
};
